import { useHotkey } from "@tanstack/react-hotkeys";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { CommandView } from "@/app/command-palette/palette-store";
import { usePaletteActions, usePaletteOpen, usePaletteView } from "@/app/command-palette/use-palette-store";

export interface UseCommandPaletteReturn {
  open: boolean;
  view: CommandView;
  searchValue: string;
  setSearchValue: (value: string) => void;
  navigate: ReturnType<typeof useNavigate>;
  handleNavigateToView: (newView: CommandView) => void;
  handleOpenChange: (open: boolean) => void;
  placeholder: string;
}

export function useCommandPalette(): UseCommandPaletteReturn {
  const navigate = useNavigate();
  const open = usePaletteOpen();
  const view = usePaletteView();
  const { open: openPalette, close, setView } = usePaletteActions();
  const [searchValue, setSearchValue] = useState("");

  // Search text is transient, local input state — it resets whenever the
  // view changes, regardless of what triggered the change.
  const prevView = useRef(view);
  useEffect(() => {
    if (prevView.current !== view) {
      prevView.current = view;
      setSearchValue("");
    }
  }, [view]);

  const handleNavigateToView = (newView: CommandView) => {
    setView(newView);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Closing from the home view leaves `view` unchanged, so the prevView
      // effect won't fire — clear the query here or it survives the reopen.
      close();
      setSearchValue("");
    } else {
      openPalette();
    }
  };

  const placeholder =
    view === "home"
      ? "Type a command or search..."
      : view === "patient-search"
        ? "Search patients..."
        : view === "colleague-search"
          ? "Search colleagues..."
          : "Search chat history...";

  // Context-dependent Escape: back to home or close
  useHotkey(
    "Escape",
    () => {
      setView("home");
      setSearchValue("");
    },
    { enabled: open && view !== "home", stopPropagation: true, conflictBehavior: "allow" },
  );

  return {
    open,
    view,
    searchValue,
    setSearchValue,
    navigate,
    handleNavigateToView,
    handleOpenChange,
    placeholder,
  };
}
